import { forwardRef, useState } from "react";
import { MapPin, Phone, Mail, Clock, CalendarCheck } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import anfahrtskarte from "@/assets/anfahrtskarte.webp";
import { useT, useLocalePath } from "@/i18n";


const Contact = forwardRef<HTMLElement>((_, ref) => {
  const lp = useLocalePath();
  const t = useT(
    {
      success: "Ihre Nachricht wurde erfolgreich gesendet!",
      failure: "Fehler beim Senden. Bitte versuchen Sie es später erneut.",
      eyebrow: "Jetzt Kontakt aufnehmen",
      heading: "Kontakt & Lage",
      intro:
        "Ich freue mich auf Ihre Nachricht. Vereinbaren Sie jetzt einen Termin für ein persönliches Gespräch.",
      formTitle: "Nachricht senden",
      firstName: "Vorname *",
      firstNamePh: "Ihr Vorname",
      lastName: "Nachname *",
      lastNamePh: "Ihr Nachname",
      email: "E-Mail *",
      emailPh: "ihre@email.at",
      phone: "Telefon",
      phonePh: "Ihre Telefonnummer",
      message: "Nachricht *",
      messagePh: "Wie kann ich Ihnen helfen?",
      sending: "Wird gesendet...",
      send: "Nachricht senden",
      addressLabel: "Adresse",
      addressValue: "Ärztezentrum West\nLinzer Straße 383/Top 7\nWien",
      phoneLabel: "Telefon",
      mailLabel: "E-Mail",
      hoursLabel: "Termine",
      hoursValue: "Nur nach Vereinbarung\nKlient:innen-Parkplatz vorhanden",
      mapAlt: "Anfahrtskarte zur Praxis Innere Balance – Linzer Straße 383, Wien",
      bookCta: "Termin online buchen",
    },
    {
      success: "Your message has been sent successfully.",
      failure: "Sending failed. Please try again later.",
      eyebrow: "Get in touch",
      heading: "Contact & location",
      intro:
        "I look forward to hearing from you. Arrange an appointment for a personal consultation.",
      formTitle: "Send a message",
      firstName: "First name *",
      firstNamePh: "Your first name",
      lastName: "Last name *",
      lastNamePh: "Your last name",
      email: "Email *",
      emailPh: "your@email.com",
      phone: "Phone",
      phonePh: "Your phone number",
      message: "Message *",
      messagePh: "How can I help you?",
      sending: "Sending...",
      send: "Send message",
      addressLabel: "Address",
      addressValue: "Ärztezentrum West\nLinzer Straße 383/Top 7\n1140 Vienna, Austria",
      phoneLabel: "Phone",
      mailLabel: "Email",
      hoursLabel: "Appointments",
      hoursValue: "By appointment only\nClient parking available",
      mapAlt: "Map showing the route to Praxis Innere Balance – Linzer Straße 383, Vienna",
      bookCta: "Book an appointment",
    },
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);

    const formData = new FormData(e.currentTarget);
    const data = {
      firstName: formData.get("firstName") as string,
      lastName: formData.get("lastName") as string,
      email: formData.get("email") as string,
      phone: formData.get("phone") as string,
      message: formData.get("message") as string,
    };

    try {
      const { error } = await supabase.functions.invoke("send-contact-email", {
        body: data,
      });

      if (error) throw error;

      toast.success(t.success);
      (e.target as HTMLFormElement).reset();
    } catch (error: any) {
      console.error("Error sending contact form:", error);
      toast.error(t.failure);
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: MapPin,
      label: t.addressLabel,
      value: t.addressValue,
    },
    {
      icon: Phone,
      label: t.phoneLabel,
      value: "0660 404 1993",
      href: "tel:+436604041993",
    },
    {
      icon: Mail,
      label: t.mailLabel,
      value: "office@ibpraxis.net",
    },
    {
      icon: Clock,
      label: t.hoursLabel,
      value: t.hoursValue,
    },
  ];

  return (
    <section ref={ref} id="kontakt" className="py-12 md:py-16 bg-background">
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mx-auto text-center mb-16">
          <span className="text-primary font-medium text-sm uppercase tracking-wider">{t.eyebrow}</span>
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mt-2 mb-6">
            {t.heading}
          </h2>
          <p className="text-lg text-muted-foreground">
            {t.intro}
          </p>
          <Button asChild size="lg" className="mt-6 gap-2">
            <Link to={lp("/termin")}>
              <CalendarCheck className="w-5 h-5" />
              {t.bookCta}
            </Link>
          </Button>
        </div>

        <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <div className="bg-card rounded-2xl p-8 border border-border">
            <h3 className="text-xl font-semibold text-foreground mb-6">{t.formTitle}</h3>
            <form className="space-y-5" onSubmit={handleSubmit}>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="firstName">{t.firstName}</Label>
                  <Input id="firstName" name="firstName" placeholder={t.firstNamePh} required />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lastName">{t.lastName}</Label>
                  <Input id="lastName" name="lastName" placeholder={t.lastNamePh} required />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">{t.email}</Label>
                <Input id="email" name="email" type="email" placeholder={t.emailPh} required />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">{t.phone}</Label>
                <Input id="phone" name="phone" type="tel" placeholder={t.phonePh} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="message">{t.message}</Label>
                <Textarea
                  id="message"
                  name="message"
                  placeholder={t.messagePh}
                  rows={5}
                  required
                />
              </div>
              <Button type="submit" className="w-full" disabled={isSubmitting}>
                {isSubmitting ? t.sending : t.send}
              </Button>
            </form>
          </div>

          {/* Contact Info */}
          <div>
            <div className="space-y-6 mb-8">
              {contactInfo.map((info) => (
                <div key={info.label} className="flex gap-4">
                  <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                    <info.icon className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="font-medium text-foreground">{info.label}</p>
                    {info.href ? (
                      <a
                        href={info.href}
                        className="text-muted-foreground whitespace-pre-line hover:text-primary transition-colors underline-offset-4 hover:underline"
                      >
                        {info.value}
                      </a>
                    ) : (
                      <p className="text-muted-foreground whitespace-pre-line">{info.value}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Anfahrtskarte */}
            <div className="rounded-xl overflow-hidden border border-border shadow-sm">
              <img 
                src={anfahrtskarte} 
                alt={t.mapAlt}
                className="w-full h-auto"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
});

Contact.displayName = "Contact";

export default Contact;
